import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subscription ,  Subject } from 'rxjs';

import { Member } from './member.model';

import { environment } from '../../environments/environment';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  apiUrl: string;
  loginUrl: string;

  clientId: any;
  clientSecret: any;
  grantType: any;

  // Login
  loginData: any;
  loginSubscription: Subscription;
  loginReceived: Subject<any> = new Subject<any>();
  loginError: Subject<any> = new Subject<any>();

  // Profile
  profileSubscription: Subscription;
  profileReceived: Subject<any> = new Subject<any>();
  profileError: Subject<any> = new Subject<any>();

  private memberModel: Member = new Member();
  private memberSource = new BehaviorSubject({});
  membersDataSubscription = this.memberSource.asObservable();

  // Space thumb upload
  spaceThumbUploadUrl: any;
  spaceAudioUploadUrl: any;

  companyRoles = new BehaviorSubject({});


  constructor(public router: Router, private http: HttpClient) {
    this.apiUrl = environment.apiUrl;
    this.loginUrl = `${this.apiUrl}/auth/login`;

    this.spaceThumbUploadUrl = `${this.apiUrl}/member/space/upload`;
    this.spaceAudioUploadUrl = `${this.apiUrl}/member/space/audio`;
    this.clientId = environment.clientId;
    this.clientSecret = environment.clientSecret;
    this.grantType = 'password';

  }

  isLoggedIn(): boolean {
    return localStorage.getItem('isLoggedIn') && this.getAccessToken() !== '' ? true : false;
  }

  isSubscribed(): boolean {
    if (this.isCollaborator()) {
      return true;
    } else {
      return localStorage.getItem('isSubscribed') && localStorage.getItem('isSubscribed') === '1' ? true : false;
    }
  }

  isCollaborator(): boolean {
    return localStorage.getItem('isCollaborator') ? true : false;
  }

  setSubscribed(value) {
    return localStorage.setItem('isSubscribed', value);
  }

  getAccessToken(): string {
    return localStorage.getItem('access_token') ? localStorage.getItem('access_token') : '';
  }

  getRefreshToken(): string {
    return localStorage.getItem('refresh_token') ? localStorage.getItem('refresh_token') : '';
  }

  getUserData(): any {
    if (localStorage.getItem('userData')) {
      try {
        return JSON.parse(localStorage.getItem('userData'));
      } catch {
        return undefined;
      }
    }
    return undefined;
  }

  logout() {
    this.removeLocalStorage();
  }

  removeLocalStorage(): void {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('isSubscribed');
    localStorage.removeItem('userData');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_type');
    localStorage.removeItem('subscription_status');
    localStorage.removeItem('company_id');
    localStorage.removeItem('isCollaborator');
    localStorage.removeItem('company_roles');
    localStorage.removeItem('billing_permission');
    localStorage.removeItem('invoice_permission');
  }


  login(loginCredentials: any) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json'
    });
    const options = {
      headers
    };
    this.loginSubscription = this.http.post(this.loginUrl, loginCredentials, options).subscribe(
      (response: any) => {
          try {
              this.loginData = response;
          } catch (e) {
              this.loginError.next(this);
          }
      },
      (error) => {
          this.loginError.next(error);
          this.loginSubscription.unsubscribe();
      },
      () => {
          this.loginReceived.next(this.loginData);
          this.loginSubscription.unsubscribe();
      }
    );
  }

  profile(accessTokenData) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${accessTokenData.data.access_token}`
    });
    const options = {
      headers,
      method: 'get'
    };
    this.profileSubscription = this.http.get(`${this.apiUrl}/member`, options).subscribe(
      (response: any) => {
          try {
              const responseData = {
                id: response.data.id,
                email: response.data.email,
                first_name: response.data.first_name,
                last_name: response.data.last_name,
                phone: response.data.phone,
                company_name: response.data.company_name,
                subscription_status: response.data.subscription_status,
                is_collaborator: response.data && response.data.is_collaborator ? response.data.is_collaborator : ''
              };
              this.setMembersData(responseData);
              localStorage.setItem('isLoggedIn', 'true');
              localStorage.setItem('userData', JSON.stringify(responseData));
              localStorage.setItem('access_token', accessTokenData.data.access_token);
              localStorage.setItem('refresh_token', accessTokenData.data.refresh_token);
              localStorage.setItem('token_type', 'Bearer');
              localStorage.setItem('isSubscribed', response.data.is_subscribed ? response.data.is_subscribed : 0);
              localStorage.setItem('isCollaborator', response.data.is_collaborator ? response.data.is_collaborator : false);
          } catch (e) {
              this.profileError.next(this);
          }
      },
      (error) => {
          let errors;
          try {
            errors = error.error.errors ? error.error.errors : [];
          } catch (e) {
            errors = 'Invalid username / password';
          }
          this.profileError.next(errors);
          this.profileSubscription.unsubscribe();
      },
      () => {
          this.profileReceived.next(this);
          this.profileSubscription.unsubscribe();
      }
    );
  }

  setMembersData(data) {
    this.memberSource.next(data);
    localStorage.setItem('userData', JSON.stringify(data));
  }

  updateSpaceThumb(accessToken, image) {
    const headers = new HttpHeaders({
      Authorization: accessToken
    });
    const formData = new FormData();
    formData.append('image', image);
    return this.http.post(this.spaceThumbUploadUrl, formData, { headers });
  }

  updateSpaceAudio(accessToken, audio) {
    const headers = new HttpHeaders({
      Authorization: accessToken
    });
    const formData = new FormData();
    formData.append('file', audio);
    return this.http.post(this.spaceAudioUploadUrl, formData, { headers });
  }

  getSubscriptionStatus() {

    if (this.isCollaborator()) {
      return true;
    } else {
      const userData = this.getUserData() ? this.getUserData() : undefined;
      if (userData && userData.subscription_status) {
        return userData.subscription_status;
      }
    }

    return undefined;
  }

  setSubscriptionStatus(value) {
    const userData = this.getUserData() ? this.getUserData() : undefined;
    if (userData) {
      userData.subscription_status = value;
      return localStorage.setItem('userData', JSON.stringify(userData));
    }
  }

  setCompanyId(value) {
    return localStorage.setItem('company_id', value);
  }

  getCompanyId() {
    return localStorage.getItem('company_id') ? localStorage.getItem('company_id') : '';
  }

  setCompanyRoles(value) {
    this.companyRoles.next(value);
    return localStorage.setItem('company_roles', value);
  }

  setBillingAccess(value) {
    return localStorage.setItem('billing_permission', value);
  }

  setInvoiceAccess(value) {
    return localStorage.setItem('invoice_permission', value);
  }

  getCompanyRoles() {
    return localStorage.getItem('company_roles') ? localStorage.getItem('company_roles') : '';
  }

  getBillingAccess() {
    return localStorage.getItem('billing_permission') && localStorage.getItem('billing_permission') === 'true' ? true : false;
  }

  getInvoiceAccess() {
    return localStorage.getItem('invoice_permission') && localStorage.getItem('invoice_permission') === 'true' ? true : false;
  }

}
